import { useState, useEffect } from 'react';
import { colors } from '../../styles/colors';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { 
  Box, 
  TextField, 
  IconButton, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Paper,
  Snackbar,
  Alert
} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Transaction, TransactionFilter, getTransactions, togglePendingStatus } from '../../services/transactionService';
import { formatDateForDisplay, formatDateForApi, formatDateForDatePicker } from '../../utils/dateUtils';
import {
  DataProviderInputMappingColumnsModel,
  getDataProviderInputMappingColumns
} from "../../services/dataProviderInputMappingService.ts";
import {lowerFirstLetter} from "../../utils/nameUtils.ts";

interface TransactionTableProps {
  initialFilter?: {
    status?: string,
    dateFilledStart?: string;
    dateFilledEnd?: string;
    dateReceivedByNASHStart?: string;
    dateReceivedByNASHEnd?: string;
  };
}



const TransactionTable = ({ initialFilter }: TransactionTableProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataProviderMappings, setDataProviderMappings] = useState<DataProviderInputMappingColumnsModel[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [searchText, setSearchText] = useState('');
  const [filter, setFilter] = useState<TransactionFilter>(() => {
    const uaid = searchParams.get('uaid');
    if (initialFilter || uaid) {
      return {
        ...initialFilter,
        uaid: uaid || undefined,
      };
    }
    return {};
  });

  useEffect(() => {
    if (filter.uaid) {
      setSearchText(filter.uaid);
    }
  }, []);

  useEffect(() => {
    const fetchDataProviderMappings = async () => {
      try{
        const mappingsData = await getDataProviderInputMappingColumns('Nash Default');
        setDataProviderMappings(mappingsData);
      } catch (error) {
        console.log('Error fetching data provider mappings', error);
      } finally {
        setLoading(false);
      }
    }
    
    fetchDataProviderMappings();
  }, []);

  
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });
  
  const handleTogglePendingStatus = async (transaction: Transaction) => {
    try {
      const newPendingStatus = !transaction.pending;
      await togglePendingStatus(transaction.id, newPendingStatus);
      await fetchTransactions();
    } catch (error) {
      console.error('Failed to toggle pending status:', error);
      setFeedback({
        open: true,
        message: 'Failed to toggle pending status. Please try again.',
        severity: 'error'
      });
    }
  }

  const defaultColumns: GridColDef[] = [
    { 
      field: "actions", 
      headerName: 'Actions', 
      width: 140,
      renderCell: (params: GridRenderCellParams<Transaction>) => {
        return (
          <Box>
            <IconButton 
              onClick={() => handleTogglePendingStatus(params.row)}
              size="small"
              title="Toggle Pending Status"
            >
              <RestartAltIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleEditNavigation(params.row.id)}
              size="small"
              title="Edit Transaction"
            >
              <EditIcon />
            </IconButton>
          </Box>
        )
      }
    },
    { 
      field: 'fileOutputStatus', 
      headerName: 'Status', 
      width: 235,
      renderCell: (params: GridRenderCellParams<Transaction>) => {
        let text;
        let color;

        if (params.row.pending) {
          text = 'Delivery Pending';
          color = colors.pending;
        } else if (params.row.fileOutputId) {
          if (params.row.fileOutputStatus === 'Delivered') {
            text = `${params.row.fileOutputStatus} on ${formatDateForDisplay(params.row.dateLogCreatedBySystem)}`;
            color = colors.success;
          } else {
            text = `${params.row.fileOutputStatus} on ${formatDateForDisplay(params.row.dateLogCreatedBySystem)}`;
            color = params.row.fileOutputStatus === 'Created' ? colors.pending : colors.error;
          }
        } else {
          text = 'Processing';
          color = colors.pending;
        }

        return (
          <Box sx={{ color }}>
            {text}
          </Box>
        );
      },
    },
    {
      field: 'pbmName',
      headerName: 'PBM Name',
      width: 200,
      renderCell: (params: GridRenderCellParams<Transaction>) => params.row.pbmName || ''
    },
    {
      field: 'dateReceivedByNASH',
      headerName: 'Date Received',
      width: 200,
      renderCell: (params: GridRenderCellParams<Transaction>) => formatDateForDisplay(params.row.dateReceivedByNASH)
    },
    {
      field: 'uaid',
      headerName: 'File ID',
      width: 200,
      renderCell: (params: GridRenderCellParams<Transaction>) => params.row.uaid || '-'
    }
  ];

  useEffect(() => {
    const providerColumns = dataProviderMappings.map(mapping => ({
      field: lowerFirstLetter(mapping.destinationColumn),
      headerName: mapping.inputColumn,
      width: 200,
      renderCell: (params: GridRenderCellParams<Transaction>) => {
        const value = params.row[lowerFirstLetter(mapping.destinationColumn)] || ''
        if(mapping.destinationColumn.toLowerCase().includes('date'))
          return formatDateForDisplay(value);

        return value;
      }
    }));
    const allColumns = [...defaultColumns, ...providerColumns];
    setColumns(allColumns);
  }, [dataProviderMappings]);
  
  


  useEffect(() => {
    fetchTransactions();
  }, [filter]);

  const fetchTransactions = async () => {
    try {
      const data = await getTransactions(filter);
      setTransactions(data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setFeedback({
        open: true,
        message: 'Failed to fetch transactions. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof TransactionFilter, value: string | null) => {
    if (field === 'uaid') {
      setSearchText(value || '');
      setFilter(prev => ({
        ...prev,
        [field]: (!value || value.length < 3) ? undefined : value
      }));
      return;
    }

    if (
      field === 'dateFilledStart' ||
      field === 'dateFilledEnd' ||
      field === 'dateReceivedByNASHStart' ||
      field === 'dateReceivedByNASHEnd'
    ) {

      setFilter(prev => ({
        ...prev,
        [field]: value ? formatDateForApi(value) : undefined
      }));
    } else {
      setFilter(prev => ({
        ...prev,
        [field]: value || undefined
      }));
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setFilter({});
  };

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  const handleEditNavigation = (id: number) => {
    navigate(`/transaction/${id}`)
  }

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Status</InputLabel>
            <Select
              size="small"
              value={filter.status || ''}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Delivered">Delivered</MenuItem>
              <MenuItem value="Error">Failed</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Created">Created</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>PBM Name</InputLabel>
            <TextField
              size="small"
              value={filter.pbmName || ''}
              onChange={(e) => handleFilterChange('pbmName', e.target.value)}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>File ID</InputLabel>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => handleFilterChange('uaid', e.target.value)}
              placeholder="Type 3 or more characters"
            />
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Date Filled Range</InputLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateFilledStart)}
                onChange={(e) => handleFilterChange('dateFilledStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Start"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateFilledEnd)}
                onChange={(e) => handleFilterChange('dateFilledEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="End"
                sx={{ flex: 1 }}
              />
            </Box>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Date Received Range</InputLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateReceivedByNASHStart)}
                onChange={(e) => handleFilterChange('dateReceivedByNASHStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Start"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateReceivedByNASHEnd)}
                onChange={(e) => handleFilterChange('dateReceivedByNASHEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="End"
                sx={{ flex: 1 }}
              />
            </Box>
          </FormControl>

          <IconButton 
            onClick={clearFilters}
            size="small"
            title="Clear Filters"
            sx={{mt:4}}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Box>

        <DataGrid
        rows={transactions}
        columns={columns}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[10, 25, 50]}
        autoHeight
        sx={{ cursor: 'pointer' }}
      />
      </Paper>
      <Snackbar 
        open={feedback.open} 
        autoHideDuration={6000} 
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseFeedback} 
          severity={feedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TransactionTable;
